import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, UserX, Wallet, Home as HomeIcon, Banknote, PhoneCall,
  AlertTriangle, TrendingUp, Fuel, Clock, HandCoins, CalendarCheck, Activity,
  MapPin, RefreshCw, Calendar, ArrowRight, ShieldCheck, ChevronRight,
  BarChart3, PieChart as PieIcon, Award, Stethoscope, CheckCircle2, Star,
  Car, Navigation, Check, Phone, MessageCircle, ExternalLink, Calculator
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, todayIST, fmtDate, telLink, waLink, initials } from "@/lib/format";
import { PageHeader, StatCard, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

const COLORS = ["#27AE60", "#1565C0", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#64748B"];

function ChartCard({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <Card className="p-4 lg:p-5 shadow-sm border-slate-200 bg-white flex flex-col justify-between" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-display font-bold text-slate-800 text-sm flex items-center gap-1.5">
            {Icon && <Icon className="h-4 w-4 text-bk-blue" />}
            {title}
          </p>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="h-64 w-full">{children}</div>
    </Card>
  );
}

// ============================================================ DEDICATED PHYSIOTHERAPIST DASHBOARD
function PhysioDashboardView({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/physio");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <Loading label="Loading your daily home visits & collections..." />;

  const today = data?.today || {};
  const month = data?.month || {};
  const todayVisits = data?.today_visits || [];

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-5 text-white shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-2xl">
            Welcome, Dr. {user?.name || "Physiotherapist"} 👋
          </h1>
          <p className="text-emerald-100 text-xs sm:text-sm mt-1">
            Your daily home visit schedule, collections, deposit liability and assigned patients
          </p>
          <p className="text-emerald-200 text-[11px] mt-1 font-medium">
            Today: {fmtDate(todayIST())}
          </p>
        </div>
        <Button
          size="sm"
          onClick={loadData}
          variant="outline"
          className="bg-white/10 hover:bg-white/20 text-white border-white/20 gap-1.5 self-start sm:self-auto"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Section 1: Today's Operations KPI Cards */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Car className="h-4 w-4 text-emerald-600" /> Today's Field Operations
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
          <StatCard
            testid="kpi-physio-today-visits"
            label="Today's Visits"
            value={today.visits || 0}
            icon={HomeIcon}
            tone="blue"
            sub={`${today.completed || 0} Completed · ${today.pending || 0} Pending`}
            onClick={() => navigate("/home-visits")}
          />
          <StatCard
            testid="kpi-physio-today-collection"
            label="Collected By Me"
            value={inr(today.collected_by_me || 0)}
            icon={Wallet}
            tone="green"
            sub="Cash & UPI collected today"
            onClick={() => navigate("/collections")}
          />
          <StatCard
            testid="kpi-physio-pending-deposit"
            label="Pending Deposit"
            value={inr(today.pending_deposit || 0)}
            icon={Banknote}
            tone={today.pending_deposit > 0 ? "danger" : "green"}
            sub="Cash in hand to deposit to clinic"
            onClick={() => navigate("/collections")}
          />
          <StatCard
            testid="kpi-physio-petrol"
            label="Today's Petrol Expense"
            value={inr(today.petrol_expense || 0)}
            icon={Fuel}
            tone="slate"
            sub="Travel expense logged"
            onClick={() => navigate("/expenses")}
          />
        </div>
      </div>

      {/* Section 2: Quick Action Shortcuts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button
          onClick={() => navigate("/home-visits")}
          className="h-12 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 shadow-sm flex items-center justify-start gap-2.5 px-4 font-semibold text-xs"
        >
          <div className="p-2 bg-blue-50 text-bk-blue rounded-lg">
            <Car className="h-4 w-4" />
          </div>
          <span>Today's Visits</span>
        </Button>

        <Button
          onClick={() => navigate("/collections")}
          className="h-12 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 shadow-sm flex items-center justify-start gap-2.5 px-4 font-semibold text-xs"
        >
          <div className="p-2 bg-emerald-50 text-bk-green rounded-lg">
            <HandCoins className="h-4 w-4" />
          </div>
          <span>Record Collection</span>
        </Button>

        <Button
          onClick={() => navigate("/expenses")}
          className="h-12 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 shadow-sm flex items-center justify-start gap-2.5 px-4 font-semibold text-xs"
        >
          <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
            <Fuel className="h-4 w-4" />
          </div>
          <span>Log Petrol Expense</span>
        </Button>

        <Button
          onClick={() => navigate("/settlement")}
          className="h-12 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 shadow-sm flex items-center justify-start gap-2.5 px-4 font-semibold text-xs"
        >
          <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
            <Calculator className="h-4 w-4" />
          </div>
          <span>My Settlement</span>
        </Button>
      </div>

      {/* Section 3: Monthly Performance */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Award className="h-4 w-4 text-amber-500" /> This Month's Performance Summary
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Completed Visits</p>
              <p className="font-display font-extrabold text-2xl text-slate-800 mt-1">
                {month.visits || 0}
              </p>
              <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Successful home sessions</p>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="h-6 w-6" />
            </div>
          </Card>

          <Card className="p-4 bg-white border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Collections</p>
              <p className="font-display font-extrabold text-2xl text-slate-800 mt-1">
                {inr(month.collection || 0)}
              </p>
              <p className="text-[11px] text-bk-blue font-medium mt-0.5">Attributed revenue</p>
            </div>
            <div className="p-3 bg-blue-50 text-bk-blue rounded-xl">
              <Wallet className="h-6 w-6" />
            </div>
          </Card>

          <Card
            onClick={() => navigate("/patients")}
            className="p-4 bg-white border-slate-200 shadow-sm flex items-center justify-between cursor-pointer hover:shadow-md transition-all"
          >
            <div>
              <p className="text-xs text-slate-500 font-medium">Assigned Patients</p>
              <p className="font-display font-extrabold text-2xl text-slate-800 mt-1">
                {month.assigned_patients || 0}
              </p>
              <p className="text-[11px] text-purple-600 font-medium mt-0.5">Under your care</p>
            </div>
            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
              <Users className="h-6 w-6" />
            </div>
          </Card>
        </div>
      </div>

      {/* Section 4: Today's Assigned Home Visits List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <CalendarCheck className="h-4 w-4 text-bk-blue" /> Today's Assigned Home Visits ({todayVisits.length})
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/home-visits")}
            className="h-6 text-[11px] text-bk-blue gap-1"
          >
            Manage All Visits <ChevronRight className="h-3 w-3" />
          </Button>
        </div>

        {todayVisits.length === 0 ? (
          <Card className="p-6 text-center bg-slate-50 border-slate-200">
            <HomeIcon className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700">No home visits scheduled for today</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Visits assigned by admin will appear here automatically.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {todayVisits.map((v) => {
              const isCompleted = v.status === "completed";
              const isStarted = v.status === "started";
              const mapsUrl = v.google_maps_url || (v.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.address + " " + (v.locality || ""))}` : null);

              return (
                <Card
                  key={v.id}
                  className={`p-4 border-slate-200 bg-white hover:shadow-md transition-all flex flex-col justify-between relative overflow-hidden ${
                    isCompleted ? "border-emerald-300 ring-1 ring-emerald-100" : ""
                  }`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-1.5 ${
                    isCompleted ? "bg-emerald-500" : isStarted ? "bg-blue-500" : "bg-amber-400"
                  }`} />

                  <div className="space-y-2.5 pt-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold text-slate-900 text-sm">{v.patient_name}</p>
                        <p className="text-[11px] text-slate-400 font-mono">{v.patient_code}</p>
                      </div>
                      <Badge variant="outline" className={`text-xs capitalize ${
                        isCompleted ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        isStarted ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {v.status}
                      </Badge>
                    </div>

                    <div className="text-xs text-slate-600 space-y-1">
                      {v.time_slot && (
                        <p className="flex items-center gap-1.5 font-medium text-slate-700">
                          <Clock className="h-3.5 w-3.5 text-bk-blue shrink-0" /> Time: {v.time_slot}
                        </p>
                      )}
                      {(v.address || v.locality) && (
                        <p className="flex items-start gap-1.5 text-slate-500">
                          <MapPin className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{v.address || v.locality}</span>
                        </p>
                      )}
                      {v.problem && (
                        <p className="text-[11px] text-slate-500 italic">Condition: {v.problem}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-700">Fee: {inr(v.visit_fee)}</span>
                    <div className="flex items-center gap-1.5">
                      {mapsUrl && (
                        <a href={mapsUrl} target="_blank" rel="noreferrer" title="Open in Google Maps">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-200 gap-1">
                            <Navigation className="h-3 w-3" /> Map
                          </Button>
                        </a>
                      )}
                      {v.phone && (
                        <a href={telLink(v.phone)} title="Call Patient">
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-blue">
                            <Phone className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      )}
                      {v.whatsapp && (
                        <a href={waLink(v.whatsapp, `Namaste ${v.patient_name} ji, BK Spine Care se aapke home visit ke baare me.`)} target="_blank" rel="noreferrer" title="WhatsApp Patient">
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-green">
                            <MessageCircle className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      )}
                      <Button
                        size="sm"
                        onClick={() => navigate("/home-visits")}
                        className="h-7 px-2 text-[11px] bg-bk-blue hover:bg-blue-700 text-white"
                      >
                        Details
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================ ADMIN / RECEPTIONIST DASHBOARD
function AdminDashboardView() {
  const navigate = useNavigate();

  const [dateRangeType, setDateRangeType] = useState("today");
  const [dateFrom, setDateFrom] = useState(todayIST());
  const [dateTo, setDateTo] = useState(todayIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyRange = (type) => {
    setDateRangeType(type);
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);

    if (type === "today") {
      const t = todayIST();
      setDateFrom(t);
      setDateTo(t);
    } else if (type === "yesterday") {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const yStr = iso(y);
      setDateFrom(yStr);
      setDateTo(yStr);
    } else if (type === "this_week") {
      const curr = new Date(today);
      const first = curr.getDate() - curr.getDay() + (curr.getDay() === 0 ? -6 : 1);
      const firstDay = new Date(curr.setDate(first));
      setDateFrom(iso(firstDay));
      setDateTo(todayIST());
    } else if (type === "this_month") {
      const mStr = todayIST().slice(0, 7);
      setDateFrom(`${mStr}-01`);
      setDateTo(todayIST());
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        range: dateRangeType,
        date_from: dateFrom,
        date_to: dateTo,
      };
      const res = await api.get("/dashboard", { params });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [dateRangeType, dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const kpis = data?.kpis || {};
  const charts = data?.charts || {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Overview Dashboard"
        subtitle="Clinic operations, multi-physiotherapist home visits, financial tracking and performance analytics"
        testid="admin-dashboard"
        action={
          <Button
            size="sm"
            onClick={loadData}
            variant="outline"
            className="gap-1.5 bg-white"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {/* Filter Bar */}
      <Card className="p-3.5 bg-slate-50/90 border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Quick Date Range Buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 mr-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> Date Range:
            </span>
            <Button
              size="sm"
              variant={dateRangeType === "today" ? "default" : "outline"}
              onClick={() => applyRange("today")}
              className={`h-8 text-xs ${dateRangeType === "today" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "yesterday" ? "default" : "outline"}
              onClick={() => applyRange("yesterday")}
              className={`h-8 text-xs ${dateRangeType === "yesterday" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              Yesterday
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "this_week" ? "default" : "outline"}
              onClick={() => applyRange("this_week")}
              className={`h-8 text-xs ${dateRangeType === "this_week" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              This Week
            </Button>
            <Button
              size="sm"
              variant={dateRangeType === "this_month" ? "default" : "outline"}
              onClick={() => applyRange("this_month")}
              className={`h-8 text-xs ${dateRangeType === "this_month" ? "bg-bk-blue text-white" : "bg-white"}`}
            >
              This Month
            </Button>
          </div>

          {/* Custom Date Pickers */}
          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setDateRangeType("custom");
              }}
              className="h-8 text-xs w-32 bg-white"
            />
            <span className="text-xs text-slate-400">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setDateRangeType("custom");
              }}
              className="h-8 text-xs w-32 bg-white"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Loading label="Computing clinic & home analytics..." />
      ) : (
        <>
          {/* Section 1: Clinic Operations */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <CalendarCheck className="h-4 w-4 text-bk-blue" /> Clinic Operations
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/clinic")}
                className="h-6 text-[11px] text-bk-blue gap-1"
              >
                Go to Today's Clinic <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard
                testid="kpi-clinic-expected"
                label="Clinic Expected"
                value={kpis.clinic_expected || 0}
                icon={CalendarCheck}
                tone="blue"
                onClick={() => navigate("/clinic")}
              />
              <StatCard
                testid="kpi-clinic-present"
                label="Clinic Present"
                value={kpis.clinic_present || 0}
                icon={UserCheck}
                tone="green"
                sub={`Pending: ${kpis.clinic_pending || 0}`}
                onClick={() => navigate("/present")}
              />
              <StatCard
                testid="kpi-clinic-absent"
                label="Clinic Absences"
                value={(kpis.clinic_manual_absent || 0) + (kpis.clinic_auto_absent || 0)}
                icon={UserX}
                tone="danger"
                sub={`Manual: ${kpis.clinic_manual_absent || 0} · Auto-Cutoff: ${kpis.clinic_auto_absent || 0}`}
                onClick={() => navigate("/absent")}
              />
              <StatCard
                testid="kpi-clinic-rescheduled"
                label="Resched / Cancelled"
                value={(kpis.clinic_rescheduled || 0) + (kpis.clinic_cancelled || 0)}
                icon={Clock}
                tone="warning"
                sub={`Resched: ${kpis.clinic_rescheduled || 0} · Cancel: ${kpis.clinic_cancelled || 0}`}
                onClick={() => navigate("/clinic")}
              />
              <StatCard
                testid="kpi-clinic-followups"
                label="Clinic Follow-ups"
                value={kpis.pending_followups || 0}
                icon={PhoneCall}
                tone="warning"
                sub="Absence & Due queue"
                onClick={() => navigate("/follow-ups")}
              />
              <StatCard
                testid="kpi-clinic-reviews"
                label="Google Reviews"
                value={kpis.review_requests_count || 0}
                icon={Star}
                tone="blue"
                sub="4+ sessions patients"
                onClick={() => navigate("/reviews")}
              />
            </div>
          </div>

          {/* Section 2: Home Service Operations */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <HomeIcon className="h-4 w-4 text-bk-green" /> Home Physiotherapy Operations
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/home-dashboard")}
                className="h-6 text-[11px] text-bk-green gap-1"
              >
                Go to Home Dashboard <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              <StatCard
                testid="kpi-home-expected"
                label="Home Expected Visits"
                value={kpis.home_expected || 0}
                icon={HomeIcon}
                tone="blue"
                onClick={() => navigate("/home-dashboard")}
              />
              <StatCard
                testid="kpi-home-completed"
                label="Home Completed"
                value={kpis.home_completed || 0}
                icon={UserCheck}
                tone="green"
                sub={`Started: ${kpis.home_started || 0}`}
                onClick={() => navigate("/home-visits")}
              />
              <StatCard
                testid="kpi-home-unavailable"
                label="Home Unavailable"
                value={kpis.home_unavailable || 0}
                icon={Clock}
                tone="warning"
                sub="Hold / Patient unavailable"
                onClick={() => navigate("/home-visits")}
              />
              <StatCard
                testid="kpi-home-cancelled"
                label="Home Cancelled"
                value={(kpis.home_cancelled || 0) + (kpis.home_provider_cancelled || 0)}
                icon={UserX}
                tone="danger"
                sub={`Provider: ${kpis.home_provider_cancelled || 0} · Patient: ${kpis.home_cancelled || 0}`}
                onClick={() => navigate("/home-visits")}
              />
            </div>
          </div>

          {/* Section 3: Financial Health & Reconciliation */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Wallet className="h-4 w-4 text-emerald-600" /> Collections & Financial Health
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              <StatCard
                testid="kpi-actual-collection"
                label="Actual Collections"
                value={inr(kpis.total_actual_collection || 0)}
                icon={Wallet}
                tone="green"
                sub={`Clinic ${inr(kpis.clinic_collection || 0)} · Home ${inr(kpis.home_collection || 0)}`}
                onClick={() => navigate("/payments")}
              />
              <StatCard
                testid="kpi-service-revenue"
                label="Service Revenue (Charges)"
                value={inr(kpis.service_revenue || 0)}
                icon={TrendingUp}
                tone="blue"
                sub="Delivered sessions value"
                onClick={() => navigate("/ledger")}
              />
              <StatCard
                testid="kpi-outstanding-due"
                label="Outstanding Patient Due"
                value={inr(kpis.total_outstanding_due || 0)}
                icon={AlertTriangle}
                tone="danger"
                sub="Receivable from patients"
                onClick={() => navigate("/patients")}
              />
              <StatCard
                testid="kpi-credit-liability"
                label="Patient Credit Liability"
                value={inr(kpis.total_credit_liability || 0)}
                icon={HandCoins}
                tone="green"
                sub="Unused patient wallet balance"
                onClick={() => navigate("/patients")}
              />
              <StatCard
                testid="kpi-pending-deposits"
                label="Pending Physio Deposits"
                value={inr(kpis.pending_physio_deposits || 0)}
                icon={Banknote}
                tone="danger"
                sub="Physio cash liability"
                onClick={() => navigate("/deposits")}
              />
              <StatCard
                testid="kpi-approved-expenses"
                label="Approved Expenses"
                value={inr(kpis.approved_expenses || 0)}
                icon={Fuel}
                tone="slate"
                sub={`Petrol: ${inr(kpis.petrol_expenses || 0)}`}
                onClick={() => navigate("/expenses")}
              />
              <StatCard
                testid="kpi-pending-followups"
                label="Pending Follow-ups"
                value={kpis.pending_followups || 0}
                icon={PhoneCall}
                tone="warning"
                sub="Absence & Due queue"
                onClick={() => navigate("/follow-ups")}
              />
              <StatCard
                testid="kpi-pending-settlements"
                label="Pending Settlements"
                value={inr(kpis.pending_settlements_payable || 0)}
                icon={ShieldCheck}
                tone="blue"
                sub="Unpaid provider share"
                onClick={() => navigate("/settlements")}
              />
            </div>
          </div>

          {/* Section 4: Rich Charts & Analytics Visuals */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Chart 1: Daily Collections & Revenue Trend */}
            <ChartCard
              title="Daily Collections & Service Revenue Trend (Last 30 Days)"
              subtitle="Cash/UPI collected vs delivered clinical service value"
              icon={TrendingUp}
              testid="chart-daily-trend"
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.daily_collection_30}>
                  <defs>
                    <linearGradient id="gColl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#27AE60" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#27AE60" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1565C0" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#1565C0" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={45} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    name="Actual Collections"
                    stroke="#27AE60"
                    strokeWidth={2}
                    fill="url(#gColl)"
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="Delivered Service Revenue"
                    stroke="#1565C0"
                    strokeWidth={2}
                    fill="url(#gRev)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Chart 2: Clinic vs Home Daily Collections */}
            <ChartCard
              title="Clinic vs Home Collections (Last 7 Days)"
              subtitle="Daily multi-channel collection breakdown"
              icon={BarChart3}
              testid="chart-clinic-home"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.clinic_vs_home_7}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={45} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="clinic" name="Clinic" fill="#27AE60" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="home" name="Home Service" fill="#1565C0" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Chart 3: Payment Modes Breakdown */}
            <ChartCard
              title="Payment Modes Distribution"
              subtitle="Cash, UPI, Card, and Bank Transfer share"
              icon={PieIcon}
              testid="chart-methods"
            >
              {charts.methods?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={charts.methods}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      label={(e) => `${e.name}: ${inr(e.value)}`}
                    >
                      {charts.methods.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">
                  No payment data in period
                </div>
              )}
            </ChartCard>

            {/* Chart 4: Physiotherapist Performance Leaderboard */}
            <ChartCard
              title="Physiotherapist Home Visits & Collections"
              subtitle="Completed visits and collection attribution by provider"
              icon={Award}
              testid="chart-physio-leaderboard"
            >
              {charts.physio_leaderboard?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.physio_leaderboard} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="completed_visits" name="Completed Visits" fill="#27AE60" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="collection" name="Collections (₹)" fill="#1565C0" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">
                  No physiotherapist visit data in period
                </div>
              )}
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

// Main Dashboard router component
export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "physiotherapist") {
    return <PhysioDashboardView user={user} />;
  }
  return <AdminDashboardView />;
}
